import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { BookOpen, Loader2, Plus, Pencil, Trash2, Save, X, Search } from 'lucide-react';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

/**
 * AdminKBEditorPanel — the headless KB editor body.
 *
 * Extracted out of `AdminKBEditorPage` so the Help Center shell can
 * mount it inside a Tabs container alongside the Support Ticket
 * triage grid without dragging the original page's full-screen
 * background + header bar in.
 *
 * No auth gate here — callers are responsible for ensuring the
 * SuperAdmin auth context is already resolved before rendering.
 */
export function AdminKBEditorPanel({ standalone = false }) {
  const [articles, setArticles] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    setLoadingList(true);
    adminApi
      .get('/v1/admin/support-articles')
      .then((r) => setArticles(r.data || []))
      .catch(() => toast.error('Could not load articles'))
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = articles.filter((a) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      a.title.toLowerCase().includes(q) ||
      a.category.toLowerCase().includes(q) ||
      (a.keywords_array || []).some((k) => k.toLowerCase().includes(q))
    );
  });

  const startNew = () => setEditing({
    id: null,
    title: '',
    category: 'getting-started',
    content_markdown: '**New article body** — supports markdown.',
    keywords_array: [],
    is_published: true,
  });

  const saveArticle = async () => {
    if (!editing.title.trim() || !editing.content_markdown.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editing.title.trim(),
        category: editing.category.trim() || 'general',
        content_markdown: editing.content_markdown,
        keywords_array: (editing.keywords_array || []).map((k) => k.trim()).filter(Boolean),
        is_published: editing.is_published !== false,
      };
      if (editing.id) {
        await adminApi.put(`/v1/admin/support-articles/${editing.id}`, payload);
        toast.success('Article updated');
      } else {
        await adminApi.post('/v1/admin/support-articles', payload);
        toast.success('Article created');
      }
      setEditing(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteArticle = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await adminApi.delete(`/v1/admin/support-articles/${a.id}`);
      toast.success('Deleted');
      if (editing?.id === a.id) setEditing(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="admin-kb-editor-panel" className={standalone ? '' : 'space-y-4'}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-white font-semibold flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-amber-400" />
            Knowledge Base Editor
          </h2>
          <p className="text-xs text-slate-400">SupportArticles powering the AI Omnibar documentation drawer</p>
        </div>
        <Button
          onClick={startNew}
          data-testid="admin-kb-new-btn"
          className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
        >
          <Plus className="w-4 h-4 mr-1" /> New Article
        </Button>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
        {/* LEFT — list pane */}
        <section className="bg-slate-900/70 border border-slate-800 rounded-2xl p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              data-testid="admin-kb-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, category, keyword…"
              className="h-9 pl-8 bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-500"
            />
          </div>
          {loadingList && (
            <div className="text-slate-400 flex items-center gap-2 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {!loadingList && filtered.length === 0 && (
            <div className="text-center text-slate-500 py-10 border border-dashed border-slate-800 rounded-xl">
              No articles yet — click "New Article" to start.
            </div>
          )}
          <div className="space-y-2">
            {filtered.map((a) => (
              <div
                key={a.id}
                data-testid={`admin-kb-row-${a.id}`}
                className={`group rounded-xl border ${editing?.id === a.id ? 'border-amber-500/60 bg-amber-500/5' : 'border-slate-800 bg-slate-950/40 hover:bg-slate-900/60'} p-3 transition-colors`}
              >
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing({ ...a, keywords_array: a.keywords_array || [] })}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`admin-kb-edit-${a.id}`}
                  >
                    <div className="text-sm font-semibold text-slate-100 truncate flex items-center gap-2">
                      {a.title}
                      {a.is_published === false && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700">draft</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 uppercase tracking-wider">{a.category}</div>
                    {(a.keywords_array || []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {a.keywords_array.slice(0, 4).map((k) => (
                          <span key={k} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">{k}</span>
                        ))}
                        {a.keywords_array.length > 4 && <span className="text-[10px] text-slate-500">+{a.keywords_array.length - 4}</span>}
                      </div>
                    )}
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditing({ ...a, keywords_array: a.keywords_array || [] })}
                      className="p-1.5 rounded text-slate-400 hover:text-amber-400 hover:bg-slate-800"
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteArticle(a)}
                      className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-900/30"
                      title="Delete"
                      data-testid={`admin-kb-delete-${a.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* RIGHT — editor pane */}
        <section className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5">
          {!editing ? (
            <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center text-slate-500">
              <BookOpen className="w-10 h-10 text-slate-700 mb-3" />
              <p className="text-sm">Pick an article on the left to edit, or click <span className="text-amber-400 font-semibold">New Article</span> to add one.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-kb-editor-pane">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-white font-semibold">
                  {editing.id ? 'Edit Article' : 'New Article'}
                </h2>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 text-xs text-slate-300 select-none">
                    <input
                      type="checkbox"
                      checked={editing.is_published !== false}
                      onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
                      data-testid="admin-kb-publish-toggle"
                      className="accent-amber-500"
                    />
                    Published
                  </label>
                  <Button
                    variant="outline"
                    onClick={() => setEditing(null)}
                    className="border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800 hover:text-white"
                    data-testid="admin-kb-cancel-btn"
                  >
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                  <Button
                    onClick={saveArticle}
                    disabled={saving}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                    data-testid="admin-kb-save-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    {editing.id ? 'Save changes' : 'Create article'}
                  </Button>
                </div>
              </div>

              <div className="grid sm:grid-cols-[1fr_180px] gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">Title</Label>
                  <Input
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    className="h-10 bg-slate-950 border-slate-700 text-slate-100"
                    data-testid="admin-kb-title-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Category</Label>
                  <Input
                    value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="h-10 bg-slate-950 border-slate-700 text-slate-100"
                    data-testid="admin-kb-category-input"
                    placeholder="getting-started / gst / inventory…"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300 text-xs">Keywords (comma-separated)</Label>
                <Input
                  value={(editing.keywords_array || []).join(', ')}
                  onChange={(e) => setEditing({
                    ...editing,
                    keywords_array: e.target.value.split(',').map((k) => k.trim()).filter(Boolean),
                  })}
                  placeholder="invoice, create, billing"
                  className="h-10 bg-slate-950 border-slate-700 text-slate-100"
                  data-testid="admin-kb-keywords-input"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Used by the omnibar's keyword-match scorer — keep them lowercase and concise.
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs">Markdown body</Label>
                  <Textarea
                    value={editing.content_markdown}
                    onChange={(e) => setEditing({ ...editing, content_markdown: e.target.value })}
                    rows={14}
                    className="bg-slate-950 border-slate-700 text-slate-100 font-mono text-xs"
                    data-testid="admin-kb-body-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Live preview</Label>
                  <div
                    data-testid="admin-kb-preview"
                    className="prose prose-sm prose-invert max-w-none p-4 bg-slate-950 border border-slate-800 rounded-md min-h-[14rem] overflow-y-auto"
                  >
                    <ReactMarkdown>{editing.content_markdown || '_Nothing to preview yet._'}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default AdminKBEditorPanel;
